/**
 * Viralidad (k-factor), coeficiente de crecimiento y Planificar — Analytics → Producto.
 * RPC: get_product_growth_loop_analytics(p_days)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';

export interface ProductShareBreakdownRow {
    event_type: string;
    count: number;
    users: number;
}

export interface ProductViralityMetrics {
    k_factor: number | null;
    k_factor_previous: number | null;
    viral_intensity: number | null;
    viral_intensity_previous: number | null;
    conversion_rate: number | null;
    conversion_rate_previous: number | null;
    conversion_rate_pct: number | null;
    attributed_signups: number;
    attributed_signups_previous: number;
    attributed_invites: number;
    attributed_share_links: number;
    viral_actions: number;
    viral_actions_previous: number;
    viral_action_users: number;
    viral_arrivals: number;
    viral_arrivals_previous: number;
    share_breakdown: ProductShareBreakdownRow[];
    invited_users: number;
    invited_users_previous: number;
    inviters: number;
    inviters_previous: number;
    active_users: number;
    registrations: number;
    invite_share_of_signups_pct: number | null;
    invite_share_of_signups_pct_previous: number | null;
    invites_per_inviter: number | null;
    shares: number;
    shares_previous: number;
    share_users: number;
    share_captures: number;
    share_captures_previous: number;
    invited_users_all_time: number;
    inviters_all_time: number;
}

export type ProductGrowthSegmentKey = 'registered' | 'activated' | 'with_captures' | 'power';

export interface ProductGrowthSegment {
    key: ProductGrowthSegmentKey | string;
    gained: number;
    lost: number;
    net: number;
    coefficient: number | null;
    base_previous: number;
}

export interface ProductGrowthHealthMetrics {
    coefficient: number | null;
    coefficient_previous: number | null;
    gained: number;
    lost: number;
    net: number;
    new_users: number;
    reactivated: number;
    cur_active: number;
    prev_active: number;
    segments: ProductGrowthSegment[];
}

export interface ProductPlannerMetrics {
    active_users: number;
    discovered_users: number;
    discovered_users_previous: number;
    discovery_pct: number | null;
    discovery_pct_previous: number | null;
    reports: number;
    reports_previous: number;
    report_users: number;
    report_users_previous: number;
    reports_per_user: number | null;
    reports_per_user_previous: number | null;
    repeat_users: number;
    repeat_pct: number | null;
    repeat_pct_previous: number | null;
    magico_runs: number;
    magico_runs_previous: number;
    report_source: 'regeneraciones' | 'aperturas' | string;
}

export interface ProductGrowthLoopAnalytics {
    period_days: number;
    comparison_label: string;
    virality: ProductViralityMetrics;
    growth: ProductGrowthHealthMetrics;
    planner: ProductPlannerMetrics;
}

function asNumber(v: unknown, fallback = 0): number {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
    }
    return fallback;
}

function asNullableNumber(v: unknown): number | null {
    if (v == null) return null;
    const n = asNumber(v, Number.NaN);
    return Number.isFinite(n) ? n : null;
}

function parseShareBreakdown(raw: unknown): ProductShareBreakdownRow[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
        .map((row) => ({
            event_type: typeof row.event_type === 'string' ? row.event_type : 'share',
            count: asNumber(row.count),
            users: asNumber(row.users),
        }));
}

function parseVirality(raw: unknown): ProductViralityMetrics {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const viralActions = asNumber(o.viral_actions ?? o.shares);
    return {
        k_factor: asNullableNumber(o.k_factor),
        k_factor_previous: asNullableNumber(o.k_factor_previous),
        viral_intensity: asNullableNumber(o.viral_intensity),
        viral_intensity_previous: asNullableNumber(o.viral_intensity_previous),
        conversion_rate: asNullableNumber(o.conversion_rate),
        conversion_rate_previous: asNullableNumber(o.conversion_rate_previous),
        conversion_rate_pct: asNullableNumber(o.conversion_rate_pct),
        attributed_signups: asNumber(o.attributed_signups ?? o.invited_users),
        attributed_signups_previous: asNumber(o.attributed_signups_previous ?? o.invited_users_previous),
        attributed_invites: asNumber(o.attributed_invites ?? o.invited_users),
        attributed_share_links: asNumber(o.attributed_share_links),
        viral_actions: viralActions,
        viral_actions_previous: asNumber(o.viral_actions_previous ?? o.shares_previous),
        viral_action_users: asNumber(o.viral_action_users ?? o.share_users),
        viral_arrivals: asNumber(o.viral_arrivals),
        viral_arrivals_previous: asNumber(o.viral_arrivals_previous),
        share_breakdown: parseShareBreakdown(o.share_breakdown),
        invited_users: asNumber(o.invited_users),
        invited_users_previous: asNumber(o.invited_users_previous),
        inviters: asNumber(o.inviters),
        inviters_previous: asNumber(o.inviters_previous),
        active_users: asNumber(o.active_users),
        registrations: asNumber(o.registrations),
        invite_share_of_signups_pct: asNullableNumber(o.invite_share_of_signups_pct),
        invite_share_of_signups_pct_previous: asNullableNumber(o.invite_share_of_signups_pct_previous),
        invites_per_inviter: asNullableNumber(o.invites_per_inviter),
        shares: viralActions,
        shares_previous: asNumber(o.shares_previous ?? o.viral_actions_previous),
        share_users: asNumber(o.share_users ?? o.viral_action_users),
        share_captures: asNumber(o.share_captures),
        share_captures_previous: asNumber(o.share_captures_previous),
        invited_users_all_time: asNumber(o.invited_users_all_time),
        inviters_all_time: asNumber(o.inviters_all_time),
    };
}

function parseGrowthSegment(raw: unknown): ProductGrowthSegment | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    const key = typeof o.key === 'string' ? o.key : '';
    if (!key) return null;
    return {
        key,
        gained: asNumber(o.gained),
        lost: asNumber(o.lost),
        net: asNumber(o.net),
        coefficient: asNullableNumber(o.coefficient),
        base_previous: asNumber(o.base_previous),
    };
}

function parseGrowth(raw: unknown): ProductGrowthHealthMetrics {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const segments = Array.isArray(o.segments)
        ? o.segments.map(parseGrowthSegment).filter((s): s is ProductGrowthSegment => s != null)
        : [];
    return {
        coefficient: asNullableNumber(o.coefficient),
        coefficient_previous: asNullableNumber(o.coefficient_previous),
        gained: asNumber(o.gained),
        lost: asNumber(o.lost),
        net: asNumber(o.net),
        new_users: asNumber(o.new_users),
        reactivated: asNumber(o.reactivated),
        cur_active: asNumber(o.cur_active),
        prev_active: asNumber(o.prev_active),
        segments,
    };
}

function parsePlanner(raw: unknown): ProductPlannerMetrics {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
        active_users: asNumber(o.active_users),
        discovered_users: asNumber(o.discovered_users),
        discovered_users_previous: asNumber(o.discovered_users_previous),
        discovery_pct: asNullableNumber(o.discovery_pct),
        discovery_pct_previous: asNullableNumber(o.discovery_pct_previous),
        reports: asNumber(o.reports),
        reports_previous: asNumber(o.reports_previous),
        report_users: asNumber(o.report_users),
        report_users_previous: asNumber(o.report_users_previous),
        reports_per_user: asNullableNumber(o.reports_per_user),
        reports_per_user_previous: asNullableNumber(o.reports_per_user_previous),
        repeat_users: asNumber(o.repeat_users),
        repeat_pct: asNullableNumber(o.repeat_pct),
        repeat_pct_previous: asNullableNumber(o.repeat_pct_previous),
        magico_runs: asNumber(o.magico_runs),
        magico_runs_previous: asNumber(o.magico_runs_previous),
        report_source: typeof o.report_source === 'string' ? o.report_source : 'aperturas',
    };
}

export function parseProductGrowthLoopResponse(raw: unknown): ProductGrowthLoopAnalytics | null {
    if (raw == null) return null;
    let payload: unknown = raw;
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }
    if (Array.isArray(payload)) payload = payload[0];
    if (!payload || typeof payload !== 'object') return null;
    const o = payload as Record<string, unknown>;
    const wrapped = o.get_product_growth_loop_analytics;
    if (wrapped && typeof wrapped === 'object') payload = wrapped;
    const p = payload as Record<string, unknown>;
    if (typeof p.period_days !== 'number' && typeof p.period_days !== 'string') return null;
    return {
        period_days: asNumber(p.period_days, 30),
        comparison_label: typeof p.comparison_label === 'string'
            ? p.comparison_label
            : 'vs. periodo anterior',
        virality: parseVirality(p.virality),
        growth: parseGrowth(p.growth),
        planner: parsePlanner(p.planner),
    };
}

export function useProductGrowthLoop(days: number = 30) {
    return useQuery<ProductGrowthLoopAnalytics>({
        queryKey: ['admin', 'product-growth-loop', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_product_growth_loop_analytics', {
                p_days: days,
            });
            if (error) throw error;
            const parsed = parseProductGrowthLoopResponse(data);
            if (!parsed) {
                throw new Error('Respuesta inválida de get_product_growth_loop_analytics');
            }
            return parsed;
        },
        retry: 1,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export function pctPointDelta(current: number | null, previous: number | null): number | null {
    if (current == null || previous == null) return null;
    return Number((current - previous).toFixed(1));
}

export function kFactorTone(k: number | null): 'good' | 'mid' | 'low' | 'empty' {
    if (k == null) return 'empty';
    if (k >= 1) return 'good';
    if (k >= 0.2) return 'mid';
    return 'low';
}

export function gFactorTone(g: number | null, lost: number, gained: number): 'good' | 'mid' | 'low' | 'empty' {
    if (lost === 0 && gained > 0) return 'good';
    if (g == null) return 'empty';
    if (g >= 1) return 'good';
    if (g >= 0.7) return 'mid';
    return 'low';
}

export const GROWTH_SEGMENT_META: Record<string, { label: string; hint: string }> = {
    registered: {
        label: 'Registrados',
        hint: 'Altas nuevas vs. cuentas que tuvieron sesión el periodo anterior y no han vuelto.',
    },
    activated: {
        label: 'Activados',
        hint: 'Completaron onboarding por primera vez vs. activados que no han vuelto.',
    },
    with_captures: {
        label: 'Con capturas',
        hint: 'Primera captura en el periodo vs. pescadores con capturas que no han vuelto.',
    },
    power: {
        label: 'Power users',
        hint: 'Cruzaron 3 capturas y 5 días activos vs. power users que no han vuelto.',
    },
};

export const SHARE_EVENT_LABELS: Record<string, string> = {
    share_capture: 'Capturas',
    share_profile: 'Perfil',
    share_moment: 'Momentos',
    share_app: 'App',
    share_app_store_link: 'Enlace tienda',
    share_club: 'Club',
    share_event: 'Evento',
    share_quiz: 'Quiz',
    share_battle: 'Batalla',
    share_stats: 'Estadísticas',
    share_species_stats: 'Ficha de especie',
    share_collection: 'Colección',
    share_patterns: 'Patrones',
    share_store_product: 'Producto tienda',
    share_top14_exported: 'Top 14',
};
